import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Phone, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Reveal listing phone only after auth (privacy).
 */
export function RevealListingPhone({
  listingId,
  phoneAvailable,
  initialPhone = null,
  className = "",
  compact = false,
  testId = "detail-phone",
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [phone, setPhone] = useState(initialPhone || "");
  const [busy, setBusy] = useState(false);

  if (!phoneAvailable && !phone) return null;

  const reveal = async () => {
    if (!user) {
      toast.info("Connectez-vous pour afficher le numéro");
      navigate(`/connexion?next=${encodeURIComponent(location.pathname)}`);
      return;
    }
    if (phone) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/listings/${listingId}/contact`);
      setPhone(data.phone);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (phone) {
    if (compact) {
      return (
        <a
          href={`tel:${phone}`}
          data-testid={testId}
          aria-label="Appeler"
          className={`inline-flex h-11 min-h-11 w-11 shrink-0 items-center justify-center rounded-md border-2 border-matpro-anthracite text-matpro-anthracite ${className}`}
        >
          <Phone className="h-4 w-4" />
        </a>
      );
    }
    return (
      <a
        href={`tel:${phone}`}
        data-testid={testId}
        className={`w-full inline-flex items-center justify-center h-11 min-h-11 rounded-md border-2 border-matpro-anthracite text-matpro-anthracite hover:bg-matpro-anthracite hover:text-white font-bold transition-colors ${className}`}
      >
        <Phone className="h-4 w-4 mr-2" /> {phone}
      </a>
    );
  }

  return (
    <button
      type="button"
      onClick={reveal}
      disabled={busy}
      data-testid={`${testId}-reveal`}
      className={
        compact
          ? `inline-flex h-11 min-h-11 w-11 shrink-0 items-center justify-center rounded-md border-2 border-matpro-anthracite text-matpro-anthracite ${className}`
          : `w-full inline-flex items-center justify-center h-11 min-h-11 rounded-md border-2 border-matpro-anthracite text-matpro-anthracite hover:bg-matpro-anthracite hover:text-white font-bold transition-colors ${className}`
      }
      aria-label="Afficher le téléphone"
    >
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Phone className="h-4 w-4" />}
      {!compact && <span className="ml-2">{busy ? "…" : "Afficher le téléphone"}</span>}
    </button>
  );
}
